import React, { FC, useCallback, useMemo } from "react";
import { useEvergisContext } from "@evergis/react";
import { IconButton } from "@evergis/ui";

import { ImageFallback } from "./ImageFallback";
import { BasemapActions, BasemapContainer, BasemapNameBlock, BasemapTitle } from "./styled";
import { useBaseMap } from "../../../../hooks/project/useBaseMap";
import { useBasemapAddDialog, useBasemapSettingsDialog } from "../../../../hooks/useDialogs";
import { useIsShared } from "../../../../hooks/api/useIsShared";

export enum BaseMapTheme {
  Light = "light",
  Dark = "dark",
}

export type BaseMapItem = {
  name: string;
  preview?: string;
  imgSrc?: string;
  title: string;
  theme: BaseMapTheme;
  opacity?: number;
  color?: string;
};

export type BasemapBlockProps = {
  onClick: VoidFunction;
  selected?: boolean;
  baseMapItem: BaseMapItem;
};

export const BasemapBlock: FC<BasemapBlockProps> = ({ onClick, selected, baseMapItem }) => {
  const { layers } = useEvergisContext();
  const { deleteMap } = useBaseMap();
  const openBasemapSettingsDialog = useBasemapSettingsDialog();
  const openBasemapColorSettingDialog = useBasemapAddDialog();
  const isShared = useIsShared();

  const onShowSettings = useCallback(() => {
    if (baseMapItem.color) {
      openBasemapColorSettingDialog([baseMapItem.name]);
    } else {
      openBasemapSettingsDialog([baseMapItem.name]);
    }
  }, [baseMapItem.name, openBasemapSettingsDialog, openBasemapColorSettingDialog, baseMapItem.color]);

  const imageLink = useMemo(
    () => baseMapItem.imgSrc || layers.getPreview(baseMapItem.name),
    [baseMapItem.imgSrc, baseMapItem.name, layers],
  );

  return (
    <BasemapContainer selected={selected} key={baseMapItem.name} onClick={onClick}>
      {!isShared && (
        <BasemapActions onClick={event => event.stopPropagation()}>
          <IconButton kind={"settings"} onClick={onShowSettings} />
          <IconButton disabled={selected} onClick={() => deleteMap(baseMapItem.name)} kind={"delete"} />
        </BasemapActions>
      )}
      <ImageFallback src={imageLink} color={baseMapItem.color} selected={selected} />
      <BasemapNameBlock>
        <BasemapTitle>{baseMapItem.title}</BasemapTitle>
      </BasemapNameBlock>
    </BasemapContainer>
  );
};
